import { z } from 'zod';
import { inferApiResultSchema } from './infer-api';

/**
 * Every decision value that has ever been valid, which is why it is stored as a `varchar` with a
 * CHECK constraint rather than a `pgEnum`: `ALTER TYPE` can add a value but cannot remove one, so
 * retiring one would mean recreating the type and rewriting the column.
 *
 * The list is append-only and the CHECK is generated from it, so no historical row can be
 * invalidated. When a value must stop being accepted on new records, split this into two - keep this
 * list as the full historical set backing the CHECK and any display mapping, and add a writable
 * subset for `CreateDemandReviewerDecisionSchema` to validate against. That is a one-line change with
 * no migration; it is not built here because there is nothing to retire yet.
 */
export const DEMAND_REVIEWER_DECISIONS = ['APPROVE', 'REJECT'] as const;
export type DemandReviewerDecision = (typeof DEMAND_REVIEWER_DECISIONS)[number];

export const DemandReviewerDecisionSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  demandReviewerId: z.number(),
  /**
   * A plain string on the way out, never an enum over the writable set: a value that has stopped
   * being accepted on new records must still deserialise on the rows already carrying it. Narrowing
   * this is what makes a response fail to validate against its own schema - see NEX-16481.
   */
  decision: z.string(),
  note: z.string().nullable(),
  createdBy: z.string().max(50),
  createdAt: z.date(),
});
export type DemandReviewerDecisionDto = z.infer<typeof DemandReviewerDecisionSchema>;

export const CreateDemandReviewerDecisionSchema = z.object({
  decision: z.enum(DEMAND_REVIEWER_DECISIONS),
  note: z.string().optional(),
});
export type CreateDemandReviewerDecisionDto = z.infer<typeof CreateDemandReviewerDecisionSchema>;

export const CreateDemandReviewerDecisionResponseSchema = inferApiResultSchema(
  DemandReviewerDecisionSchema,
);
export type CreateDemandReviewerDecisionResponseDto = z.infer<
  typeof CreateDemandReviewerDecisionResponseSchema
>;

export const GetDemandReviewerDecisionsResponseSchema = inferApiResultSchema(
  z.array(DemandReviewerDecisionSchema),
);
export type GetDemandReviewerDecisionsResponseDto = z.infer<
  typeof GetDemandReviewerDecisionsResponseSchema
>;
